import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CommandLayout from "../../components/war-room/CommandLayout";
import { useCaseFilings, type FilingRow } from "../../hooks/useCaseFilings";
import { useEvidenceAsk, type EvidenceHit } from "../../hooks/useEvidenceAsk";
import { useChunkContext } from "../../hooks/useChunkContext";
import {
  useFilingSearch,
  type ParagraphHit,
} from "../../hooks/useFilingSearch";
import EvidenceInventory from "../../components/war-room/EvidenceInventory";
import { useLex } from "../../lib/theme/ThemeProvider";

/**
 * Hell Hounds — the Eater + Retriever surface.
 *   Eater    → corpus (filings + future evidence) the hounds have eaten.
 *   Retriever → fetches matching paragraphs from the corpus by query.
 *
 * Every Retriever result cites its source paragraph (filing + section +
 * paragraph_number) so claims are traceable.
 */
function HellHounds() {
  const { caseId } = useParams<{ caseId: string }>();
  const lex = useLex();

  return (
    <CommandLayout caseId={caseId} variant="lawdogg" currentCommand="hounds">
      <header className="rounded border border-steel-dark bg-iron/70 px-6 py-6 backdrop-blur-sm">
        <p className="mb-2 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">
          {`// ${lex.commands.hounds.verb.toLowerCase()}`}
        </p>
        <h2
          className="font-display text-2xl font-light leading-tight tracking-tight text-bone md:text-3xl"
          style={{ textShadow: "0 2px 16px rgba(0,0,0,0.6)" }}
        >
          The Eater consumes. The Retriever fetches.
        </h2>
        <p className="mt-3 max-w-3xl font-display text-sm leading-relaxed text-bone-dim">
          Corpus on the left, on-demand fetch on the right. Every Retriever
          hit cites the exact paragraph it pulled from.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {caseId ? <EaterPanel caseId={caseId} /> : null}
        {caseId ? <RetrieverPanel caseId={caseId} /> : null}
      </div>

      {caseId ? <EvidenceInventory caseId={caseId} /> : null}
    </CommandLayout>
  );
}

// ============================================================
// Eater — corpus inventory
// ============================================================

function EaterPanel({ caseId }: { caseId: string }) {
  const lex = useLex();
  const { data: filings, isLoading, error } = useCaseFilings(caseId);
  const navigate = useNavigate();

  return (
    <div className="rounded border border-steel-dark bg-iron/70 backdrop-blur-sm lg:col-span-2">
      <div className="flex items-center justify-between border-b border-steel-dark/70 px-5 py-3 font-display text-base font-light uppercase tracking-[0.16em]">
        <span className="text-bone-dim">{`${lex.ui.corpusMaw} // corpus`}</span>
        <span className="text-bone-mute/70">
          {isLoading
            ? "loading"
            : error
              ? "error"
              : `${filings?.length ?? 0} filing${filings?.length === 1 ? "" : "s"}`}
        </span>
      </div>

      <div className="px-5 py-4">
        {isLoading ? (
          <Skeleton label="counting the corpus" />
        ) : error ? (
          <ErrorRow message={error.message} />
        ) : !filings || filings.length === 0 ? (
          <p className="font-display text-sm text-bone-dim">
            No filings ingested yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {filings.map((f) => (
              <EaterRow key={f.id} filing={f} />
            ))}
          </ul>
        )}

        <button
          type="button"
          onClick={() => navigate("/upload")}
          className="mt-4 w-full rounded border border-dashed border-ember-bright/40 bg-iron/40 px-4 py-3 text-center font-display text-base font-light uppercase tracking-[0.14em] text-bone hover:border-ember-bright/60 hover:bg-ember/20"
        >
          ⊕ feed the eater another filing
        </button>

        <button
          type="button"
          onClick={() => navigate(`/upload-evidence?case=${caseId}`)}
          className="mt-2 w-full rounded border border-dashed border-ember-bright/40 bg-iron/40 px-4 py-3 text-center font-display text-base font-light uppercase tracking-[0.14em] text-bone hover:border-ember-bright/60 hover:bg-ember/20"
        >
          ⊕ feed the eater evidence (email PDFs)
        </button>

        <button
          type="button"
          onClick={() => navigate(`/upload-documents?case=${caseId}`)}
          className="mt-2 w-full rounded border border-dashed border-ember-bright/40 bg-iron/40 px-4 py-3 text-center font-display text-base font-light uppercase tracking-[0.14em] text-bone hover:border-ember-bright/60 hover:bg-ember/20"
        >
          ⊕ feed the eater documents (trust / financial / regulatory)
        </button>
      </div>
    </div>
  );
}

function EaterRow({ filing }: { filing: FilingRow }) {
  return (
    <li className="rounded border border-steel-dark/70 bg-iron-2/40 px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">
          {filing.filing_kind.replace(/_/g, " ")}
        </span>
        {filing.is_operative ? (
          <span className="rounded border border-ember-bright/60 px-1.5 py-0.5 font-display text-sm font-light uppercase tracking-[0.12em] text-ember-bright">
            operative
          </span>
        ) : null}
      </div>
      <p className="mt-0.5 truncate font-display text-sm font-light text-bone">
        {filing.title}
      </p>
    </li>
  );
}

// ============================================================
// Retriever — search the paragraphs
// ============================================================

function RetrieverPanel({ caseId }: { caseId: string }) {
  const lex = useLex();
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<"ask" | "exact">("ask");
  const [submitted, setSubmitted] = useState("");

  // exact mode: the old ILIKE filing-paragraph search
  const { data: hits, isFetching, error } = useFilingSearch(caseId, mode === "exact" ? submitted : "");
  // ask mode: semantic evidence chat
  const { ask, result, asking, error: askError } = useEvidenceAsk(caseId);

  const submit = () => {
    const q = query.trim();
    if (q.length < 2) return;
    setSubmitted(q);
    if (mode === "ask") void ask(q, "ask");
  };

  return (
    <div className="flex min-h-[480px] flex-col rounded border border-steel-dark bg-iron/70 backdrop-blur-sm lg:col-span-3">
      <div className="flex items-center justify-between border-b border-steel-dark/70 px-5 py-3 font-display text-base font-light uppercase tracking-[0.16em]">
        <span className="flex items-center gap-3">
          <span className="text-bone-dim">retriever // {mode === "ask" ? "ask" : "fetch"}</span>
          <span className="flex gap-1 normal-case tracking-normal">
            <button type="button" onClick={() => setMode("ask")}
              className={`rounded border px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em] ${mode === "ask" ? "border-ember-bright/60 bg-ember/20 text-ember-bright" : "border-steel-dark text-bone-mute hover:text-bone"}`}>
              ask the corpus
            </button>
            <button type="button" onClick={() => setMode("exact")}
              className={`rounded border px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em] ${mode === "exact" ? "border-ember-bright/60 bg-ember/20 text-ember-bright" : "border-steel-dark text-bone-mute hover:text-bone"}`}>
              filings · exact text
            </button>
          </span>
        </span>
        <span className="flex items-center gap-2 text-bone-mute/70">
          <span className="inline-block h-1 w-1 animate-pulse-ember rounded-full bg-ember-bright" />
          {mode === "ask"
            ? (asking ? lex.ui.houndsOnTrail : result ? `${result.hits.length} scent${result.hits.length === 1 ? "" : "s"}` : "standing by")
            : (isFetching ? "running down a scent" : submitted ? `${hits?.length ?? 0} hit${hits?.length === 1 ? "" : "s"}` : "standing by")}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {mode === "ask" ? (
          !submitted && !result ? (
            <AskIdleHints />
          ) : asking ? (
            <Skeleton label="raking the corpus — semantic + exact, then grounding the answer" />
          ) : askError ? (
            <ErrorRow message={askError} />
          ) : result ? (
            <AskResults r={result} />
          ) : null
        ) : !submitted ? (
          <RetrieverIdleHints />
        ) : isFetching ? (
          <Skeleton label={lex.ui.houndsOnTrail} />
        ) : error ? (
          <ErrorRow message={error.message} />
        ) : !hits || hits.length === 0 ? (
          <p className="font-display text-sm text-bone-dim">
            No paragraphs matched "{submitted}".
          </p>
        ) : (
          <ul className="space-y-3">
            {hits.map((h) => (
              <RetrieverHit key={h.id} hit={h} query={submitted} />
            ))}
          </ul>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className="border-t border-steel-dark/70 px-5 py-4"
      >
        <div className="flex items-center gap-3 rounded border border-steel-dark bg-charcoal/80 px-4 py-3 focus-within:border-ember-bright/50">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={mode === "ask"
              ? "ask the evidence — did Sutton know about the salary? who handled the inventory?"
              : "exact text in the filings — case caption, statute number, party name…"}
            className="flex-1 bg-transparent font-display text-sm text-bone placeholder:text-bone-mute/60 focus:outline-none"
          />
          <button
            type="submit"
            disabled={query.trim().length < 2 || asking}
            className="border border-ember-bright/40 bg-ember/20 px-3 py-1 font-display text-base font-light uppercase tracking-[0.14em] text-bone transition-colors hover:bg-ember/40 hover:shadow-ember-soft disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mode === "ask" ? "Ask" : "Fetch"}
          </button>
        </div>
        <p className="mt-2 font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute/60">
          // answers come only from the corpus, every claim cited to source. work product. all queries audited.
        </p>
      </form>
    </div>
  );
}

function AskIdleHints() {
  return (
    <div>
      <p className="font-display text-sm text-bone-dim">
        Ask a question in plain English. The hounds run semantic + exact retrieval
        over the evidence corpus (emails, documents, records) and answer only from
        what they find — every claim cited, every citation openable to its source.
      </p>
      <ul className="mt-4 space-y-2 font-display text-base text-bone-mute/80">
        <li>// did Sutton know Kaylan was taking a salary?</li>
        <li>// who prepared the estate inventory and when?</li>
        <li>// what was said about the pharmacy valuation?</li>
        <li>// when did the parties last discuss the trust distributions?</li>
      </ul>
    </div>
  );
}

function AskResults({ r }: { r: import("../../hooks/useEvidenceAsk").AskResult }) {
  const lex = useLex();
  const [showAll, setShowAll] = useState(false);
  return (
    <div className="space-y-4">
      {r.answer && (
        <div className={`rounded border px-4 py-3 ${r.found ? "border-ember-bright/40 bg-ember/5" : "border-steel-dark bg-black/20"}`}>
          <p className="mb-1.5 font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute">
            {r.found ? `// ${lex.ui.houndsReport}` : `// ${lex.ui.houndsNotFound}`}
          </p>
          <p className="font-display text-[14px] leading-relaxed text-bone">{r.answer}</p>
        </div>
      )}
      {r.citations.length > 0 && (
        <div>
          <p className="mb-2 font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute">// cited sources — click to open</p>
          <div className="space-y-2">
            {r.citations.map((h) => <EvidenceHitRow key={h.chunk_id} h={h} />)}
          </div>
        </div>
      )}
      {r.hits.length > r.citations.length && (
        <div>
          <button type="button" onClick={() => setShowAll((v) => !v)}
            className="font-display text-sm font-light uppercase tracking-[0.1em] text-blue-bright/80 hover:text-blue-bright">
            {showAll ? "▾ hide" : `▸ all scents (${r.hits.length})`}
          </button>
          {showAll && (
            <div className="mt-2 space-y-2">
              {r.hits.map((h) => <EvidenceHitRow key={h.chunk_id} h={h} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EvidenceHitRow({ h }: { h: EvidenceHit }) {
  const [open, setOpen] = useState(false);
  const ctx = useChunkContext(h.chunk_id, open);
  return (
    <div className="rounded border border-steel-dark/70 bg-iron-2/50 px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-2 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-ember-bright" />
        <span>{h.source_label}</span>
        {h.occurred_on && <span>· {h.occurred_on}</span>}
        <span className="ml-auto text-bone-mute/60">scent {Math.round(h.score * 100)}</span>
      </div>
      <p className="mt-1 border-l-2 border-steel-dark pl-2 font-display text-[12px] leading-snug text-bone-dim">{h.snippet}</p>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mt-1.5 font-display text-sm font-light uppercase tracking-[0.1em] text-blue-bright/80 hover:text-blue-bright"
      >
        {open ? "▾ hide source" : "▸ view in source"}
      </button>
      {open && (
        <div className="mt-2 rounded border border-steel-dark/50 bg-charcoal/60 p-2">
          {ctx.isLoading && <p className="font-display text-sm text-bone-mute">// opening the source…</p>}
          {ctx.error && <p className="font-display text-sm text-ember-bright">// {ctx.error.message}</p>}
          {ctx.data && (
            <div className="space-y-1">
              {ctx.data.chunks.map((line) => (
                <pre key={line.chunk_index}
                     className={`overflow-x-auto whitespace-pre-wrap rounded px-2 py-1 font-display text-base leading-snug ${
                       line.is_center ? "bg-ember/10 text-bone shadow-ember-soft" : "text-bone-mute/70"
                     }`}>
                  {line.body}
                </pre>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RetrieverIdleHints() {
  return (
    <div>
      <p className="font-display text-sm text-bone-dim">
        Type a query, press Fetch. The hounds rake the ingested filings and
        return matching paragraphs with citations.
      </p>
      <ul className="mt-4 space-y-2 font-display text-base text-bone-mute/80">
        <li>// fiduciary duty</li>
        <li>// Co-Executor</li>
        <li>// 14-90</li>
        <li>// embezzlement</li>
      </ul>
    </div>
  );
}

function RetrieverHit({ hit, query }: { hit: ParagraphHit; query: string }) {
  return (
    <li className="rounded border border-steel-dark/70 bg-iron-2/50 px-4 py-3">
      <div className="flex items-baseline justify-between gap-3 text-bone-mute">
        <span className="font-display text-base font-light uppercase tracking-[0.12em]">
          ¶ {hit.paragraph_number} // {hit.section.section_kind.replace(/_/g, " ")}
          {hit.section.ordinal !== null ? ` // ordinal ${hit.section.ordinal}` : ""}
        </span>
        <span className="font-display text-base font-light uppercase tracking-[0.12em]">
          {hit.filing.filing_kind.replace(/_/g, " ")}
        </span>
      </div>
      {hit.section.title ? (
        <p className="mt-1 font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute/70">
          {hit.section.title}
        </p>
      ) : null}
      <p className="mt-2 font-display text-sm leading-relaxed text-bone-dim">
        {highlight(hit.body, query)}
      </p>
      {hit.target ? (
        <p className="mt-2 font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">
          target: {hit.target}
        </p>
      ) : null}
    </li>
  );
}

/** Highlight matched substrings in the paragraph body. */
function highlight(body: string, query: string): React.ReactNode {
  if (!query) return body;
  const lc = body.toLowerCase();
  const q = query.toLowerCase();
  const out: React.ReactNode[] = [];
  let i = 0;
  while (i < body.length) {
    const idx = lc.indexOf(q, i);
    if (idx === -1) {
      out.push(body.slice(i));
      break;
    }
    if (idx > i) out.push(body.slice(i, idx));
    out.push(
      <mark
        key={idx}
        className="bg-ember/40 px-0.5 text-bone shadow-ember-soft"
      >
        {body.slice(idx, idx + q.length)}
      </mark>,
    );
    i = idx + q.length;
  }
  return out;
}

// ============================================================
// Reusable bits
// ============================================================

function Skeleton({ label }: { label: string }) {
  return (
    <p className="flex items-center gap-3 font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute">
      <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow" />
      <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:200ms]" />
      <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:400ms]" />
      {label}
    </p>
  );
}

function ErrorRow({ message }: { message: string }) {
  return (
    <div>
      <p className="font-display text-base font-light uppercase tracking-[0.16em] text-ember-bright">
        // the hounds came back bloody
      </p>
      <p className="mt-1 font-display text-base text-bone-dim">{message}</p>
    </div>
  );
}

export default HellHounds;
